import { useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { INR } from "../lib/api";
import { AlertTriangle, Lock, Clock, XCircle, PauseCircle } from "lucide-react";
import { Button } from "./ui/button";

/**
 * Wraps protected app screens.
 * - pending_approval / rejected → block screen (registration gate)
 * - suspended → block screen (billing gate)
 * - grace → banner at top
 * - active → render children
 */
export default function SubscriptionGate({ children }) {
  const { user, refreshMe, logout } = useAuth();

  useEffect(() => {
    const i = setInterval(() => refreshMe && refreshMe(), 5 * 60_000);
    return () => clearInterval(i);
  }, [refreshMe]);

  const sub = user?.subscription;
  const cafe = user?.cafe;

  if (user?.role === "platform") return children;
  if (!sub || !cafe) return children;

  // --- Pending approval ---
  if (sub.status === "pending_approval") {
    return (
      <div className="min-h-screen w-full bg-[#0a0f0d] grid place-items-center p-6">
        <div
          data-testid="pending-approval-block"
          className="max-w-lg w-full bg-[#121a16] border border-amber-400/30 rounded-2xl p-8 shadow-2xl text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-500/15 border border-amber-400/40 grid place-items-center mb-4">
            <Clock className="w-7 h-7 text-amber-300 animate-pulse" />
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight">
            Waiting for Approval
          </h1>
          <p className="text-gray-400 mt-2 text-sm leading-relaxed">
            Thanks for registering <span className="text-white font-bold">{cafe.name}</span>.
            Our platform team is reviewing your application. You'll get an email
            and be able to log in as soon as you're approved.
          </p>
          <div className="mt-6 bg-black/40 border border-white/10 rounded-lg p-4 text-left space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Cafe</span>
              <span className="text-white font-bold">{cafe.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Status</span>
              <span className="text-amber-300 font-bold uppercase tracking-wider text-xs">
                Pending Review
              </span>
            </div>
            {sub.registered_at && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Submitted</span>
                <span className="text-gray-300 font-mono text-xs">
                  {new Date(sub.registered_at).toLocaleString("en-IN")}
                </span>
              </div>
            )}
          </div>
          <Button
            onClick={logout}
            className="mt-6 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            data-testid="pending-logout-btn"
          >
            Sign Out
          </Button>
          <button
            onClick={() => refreshMe && refreshMe()}
            className="block mx-auto mt-3 text-xs text-gray-500 hover:text-amber-300 uppercase tracking-widest"
          >
            Check again
          </button>
        </div>
      </div>
    );
  }

  // --- Rejected ---
  if (sub.status === "rejected") {
    return (
      <div className="min-h-screen w-full bg-[#0a0f0d] grid place-items-center p-6">
        <div
          data-testid="rejected-block"
          className="max-w-lg w-full bg-[#121a16] border border-rose-500/30 rounded-2xl p-8 shadow-2xl text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-full bg-rose-500/20 border border-rose-400/40 grid place-items-center mb-4">
            <XCircle className="w-7 h-7 text-rose-300" />
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight">
            Registration Not Approved
          </h1>
          <p className="text-gray-400 mt-2 text-sm leading-relaxed">
            Unfortunately we couldn't approve <span className="text-white font-bold">{cafe.name}</span>
            {" "}at this time.
          </p>
          {sub.rejection_reason && (
            <div className="mt-4 text-left bg-rose-500/10 border border-rose-400/30 rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-rose-300/80 font-bold mb-1">
                Reason
              </div>
              <div className="text-sm text-rose-100">{sub.rejection_reason}</div>
            </div>
          )}
          <p className="text-gray-500 text-xs mt-4">
            If you'd like to re-apply with updated details, please contact the platform team.
          </p>
          <Button
            onClick={logout}
            className="mt-6 bg-rose-500 hover:bg-rose-600 text-white font-black uppercase tracking-widest"
            data-testid="rejected-logout-btn"
          >
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  // --- Platform-initiated pause ---
  if (sub.status === "paused") {
    const pauseUntil = sub.pause_until
      ? new Date(sub.pause_until).toLocaleString("en-IN")
      : "—";
    return (
      <div className="min-h-screen w-full bg-[#0a0f0d] grid place-items-center p-6">
        <div
          data-testid="paused-block"
          className="max-w-lg w-full bg-[#121a16] border border-amber-400/40 rounded-2xl p-8 shadow-2xl text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-full bg-amber-500/20 border border-amber-400/40 grid place-items-center mb-4">
            <PauseCircle className="w-7 h-7 text-amber-300 animate-pulse" />
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight">
            Access Paused
          </h1>
          <p className="text-gray-400 mt-2 text-sm leading-relaxed">
            Access to{" "}
            <span className="text-white font-bold">{cafe.name}</span> has been
            temporarily paused by the platform team. Resumes automatically in{" "}
            <span className="text-amber-300 font-bold">
              {sub.days_remaining}
            </span>{" "}
            day{sub.days_remaining === 1 ? "" : "s"}.
          </p>
          <div className="mt-6 bg-black/40 border border-white/10 rounded-lg p-4 text-left space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Auto-resumes</span>
              <span className="text-amber-300 font-mono text-xs">
                {pauseUntil}
              </span>
            </div>
            {sub.pause_reason && (
              <div className="flex justify-between text-sm gap-3">
                <span className="text-gray-400 shrink-0">Reason</span>
                <span className="text-gray-200 text-right">{sub.pause_reason}</span>
              </div>
            )}
          </div>
          <p className="text-gray-500 text-xs mt-4">
            If this looks like a mistake, please contact your platform team.
          </p>
          <Button
            onClick={logout}
            className="mt-6 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            data-testid="paused-logout-btn"
          >
            Sign Out
          </Button>
          <button
            onClick={() => refreshMe && refreshMe()}
            className="block mx-auto mt-3 text-xs text-gray-500 hover:text-amber-300 uppercase tracking-widest"
          >
            Check again
          </button>
        </div>
      </div>
    );
  }

  // --- Subscription suspended ---
  if (sub.status === "suspended") {
    const monthly = (sub.daily_cost || 0) * 30;
    return (
      <div className="min-h-screen w-full bg-[#0a0f0d] grid place-items-center p-6">
        <div
          data-testid="subscription-block"
          className="max-w-lg w-full bg-[#121a16] border border-rose-500/30 rounded-2xl p-8 shadow-2xl text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-full bg-rose-500/20 border border-rose-400/40 grid place-items-center mb-4">
            <Lock className="w-7 h-7 text-rose-300" />
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight">
            Subscription Expired
          </h1>
          <p className="text-gray-400 mt-2 text-sm leading-relaxed">
            <span className="text-white font-bold">{cafe.name}</span> hasn't paid
            in <span className="text-rose-300 font-bold">{sub.days_overdue}</span> day{sub.days_overdue === 1 ? "" : "s"}.
            Please contact your platform owner to renew and continue using the dashboard.
          </p>
          <div className="mt-6 bg-black/40 border border-white/10 rounded-lg p-4 text-left space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Tables</span>
              <span className="text-white font-bold">{cafe.tables_count}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Daily cost</span>
              <span className="text-white font-bold">{INR(sub.daily_cost)}/day</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">≈ Monthly</span>
              <span className="text-amber-300 font-bold">{INR(monthly)}/mo</span>
            </div>
          </div>
          <Button
            onClick={logout}
            className="mt-6 bg-amber-500 hover:bg-amber-600 text-black font-black uppercase tracking-widest"
            data-testid="suspended-logout-btn"
          >
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  if (sub.status === "grace") {
    return (
      <div className="min-h-screen">
        <div
          data-testid="grace-banner"
          className="bg-gradient-to-r from-amber-500/15 to-amber-500/5 border-b border-amber-400/30 px-4 py-2 text-center text-xs text-amber-200 tracking-wider"
        >
          <AlertTriangle className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5 text-amber-300" />
          Your subscription expired{" "}
          <b>{sub.days_overdue}</b> day{sub.days_overdue === 1 ? "" : "s"} ago ·{" "}
          <b>{sub.grace_period_days - sub.days_overdue}</b> day(s) left in grace before block ·
          please pay {INR(sub.daily_cost * 30)}/mo to renew.
        </div>
        {children}
      </div>
    );
  }

  return children;
}
